import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { IntegrationVendor } from '@magnetar/data-access'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { logger } from '@magnetar/utils'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { Copy, CheckCircle, Eye, EyeOff } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { integrationVendorService } from '@/services/integration_vendor_service'
import { toast } from 'sonner'

type FormValues = {
  vendor_name: string
  referer_urls?: string
  permission_scopes?: string
  is_active: boolean
}

interface IntegrationVendorSettingDialogProps {
  vendor?: IntegrationVendor
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
}

export function IntegrationVendorSettingDialog({
  vendor,
  open,
  onOpenChange,
  onSuccess,
}: IntegrationVendorSettingDialogProps) {
  const { token } = useAuth()
  const [createdVendor, setCreatedVendor] = useState<IntegrationVendor | null>(null)
  const [showAppKey, setShowAppKey] = useState(false)

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    toast.success('憑證已複製到剪貼簿')
  }

  const handleCloseCredentials = () => {
    setCreatedVendor(null)
    setShowAppKey(false)
    onOpenChange(false)
    form.reset()
    onSuccess()
  }

  const form = useForm<FormValues>({
    defaultValues: {
      vendor_name: '',
      referer_urls: '',
      permission_scopes: '',
      is_active: true,
    },
  })

  useEffect(() => {
    if (vendor) {
      form.reset({
        vendor_name: vendor.vendor_name,
        referer_urls: vendor.referer_urls?.join('\n') || '',
        permission_scopes: vendor.permission_scopes?.join('\n') || '',
        is_active: vendor.is_active,
      })
    } else {
      form.reset({
        vendor_name: '',
        referer_urls: '',
        permission_scopes: '',
        is_active: true,
      })
    }
  }, [vendor, form])

  const onSubmit = async (values: FormValues) => {
    try {
      if (!token) {
        toast.error('認證錯誤')
        return
      }

      const data = {
        vendor_name: values.vendor_name,
        referer_urls: values.referer_urls
          ? values.referer_urls.split('\n').filter((url) => url.trim())
          : [],
        permission_scopes: values.permission_scopes
          ? values.permission_scopes.split('\n').filter((scope) => scope.trim())
          : [],
        is_active: values.is_active,
      }

      const response = vendor
        ? await integrationVendorService.updateVendor(vendor.integration_vendor_id, data, token)
        : await integrationVendorService.createVendor(data, token)

      if (!response.success) {
        throw new Error(response.error?.message || '儲存失敗')
      }

      if (!vendor && response.data) {
        // 新增成功，顯示生成的憑證
        setCreatedVendor(response.data)
        toast.success(`${data.vendor_name} 新增成功，請複製保存您的憑證`)
      } else {
        // 更新成功
        toast.success(`${data.vendor_name} 更新成功`)
        onOpenChange(false)
        form.reset()
        onSuccess()
      }
    } catch (error) {
      logger.error('儲存介接廠商失敗:', error)
      toast.error('儲存失敗')
    }
  }

  // 如果是顯示憑證模式
  if (createdVendor) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              廠商創建成功
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded">
              <h4 className="font-medium text-yellow-800 mb-2">⚠️ 重要提醒</h4>
              <p className="text-sm text-yellow-700">
                請立即複製並安全保存以下憑證。App Key 將不會再次顯示！
              </p>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-700">廠商名稱</label>
                <div className="mt-1">
                  <Badge variant="outline">{createdVendor.vendor_name}</Badge>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">App ID</label>
                <div className="mt-1 flex items-center gap-2">
                  <code className="px-2 py-1 bg-gray-100 rounded text-sm font-mono flex-1 min-w-0 break-all">
                    {createdVendor.app_id}
                  </code>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(createdVendor.app_id)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">App Key</label>
                <div className="mt-1 flex items-center gap-2">
                  <code className="px-2 py-1 bg-gray-100 rounded text-sm font-mono flex-1 min-w-0 break-all">
                    {createdVendor.app_key}
                  </code>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(createdVendor.app_key)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={handleCloseCredentials}>確認已保存</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{vendor ? '編輯介接廠商' : '新增介接廠商'}</DialogTitle>
        </DialogHeader>

        {!vendor && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-sm text-blue-700">
              <span className="font-medium">提示：</span>
              App ID 和 App Key 將在廠商建立後自動生成，請妥善保存憑證資訊。
            </p>
          </div>
        )}

        {vendor && (
          <div className="space-y-2">
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-md space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  App ID
                </label>
                <div className="mt-1 flex items-center gap-2">
                  <p className="text-sm font-mono text-gray-900 break-all flex-1">{vendor.app_id}</p>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 shrink-0"
                    onClick={() => copyToClipboard(vendor.app_id)}
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  App Key
                </label>
                <div className="mt-1 flex items-center gap-2">
                  {showAppKey ? (
                    <code className="text-sm font-mono text-gray-900 break-all flex-1">
                      {vendor.app_key}
                    </code>
                  ) : (
                    <p className="text-sm text-gray-400 flex-1">{'•'.repeat(32)}</p>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 shrink-0"
                    onClick={() => setShowAppKey(!showAppKey)}
                  >
                    {showAppKey ? (
                      <EyeOff className="h-3.5 w-3.5" />
                    ) : (
                      <Eye className="h-3.5 w-3.5" />
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 shrink-0"
                    onClick={() => copyToClipboard(vendor.app_key)}
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="vendor_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>廠商名稱</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="referer_urls"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>允許來源網址 (每行一個)</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder="https://example.com&#10;https://app.example.com"
                      rows={3}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="permission_scopes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>權限範圍 (每行一個)</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder="read:appointments&#10;write:appointments&#10;read:clinics"
                      rows={3}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">啟用狀態</FormLabel>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="submit">{vendor ? '儲存' : '新增'}</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
